import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import { errorCode } from '@/api/errors';
import type { components } from '@/api/qraft/schema';
import { AlertBox } from '@/common/AlertBox';
import { FormFieldLabel } from '@/common/forms/FormInput';
import { InfoSectionContainer } from '@/common/InfoSection';
import { LargeArrowBackButton } from '@/common/LargeArrowBackButton';
import { TabsBar } from '@/common/TabsBar';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { WBSButton } from '@/common/WBSButton';
import { CheckboxLabel, WBSCheckbox } from '@/common/WBSCheckbox';
import {
  BulkStudentConfirmModal,
  SKIP_REASON_KEYS,
} from '@/groups/coordinator/BulkStudentConfirmModal';
import { useAdminTabs } from '@/groups/coordinator/useAdminTabs';
import {
  noop,
  useGroupStudentPicker,
} from '@/groups/coordinator/useGroupStudentPicker';
import { PageContent } from '@/settings/settingsStyles';
import { PEOPLE_PICKER_MIN_QUERY } from '~/hooks/useAsyncPeoplePicker';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { SearchSelect } from '~/styles/forms/SearchSelect';
import { GroupHeaderWrapper } from '~/styles/layoutElements';
import { formatString } from '~/utils/stringFormat';

type GroupReleaseRequest = components['schemas']['GroupReleaseRequest'];
type GroupReleaseResult = components['schemas']['GroupReleaseResult'];

/**
 * A count ternary, not an i18next plural: all four locale files hold zero
 * `_one`/`_other` keys and `i18n.ts` declares no plural config. Both values carry
 * legacy's `%d` verbatim, which is why `formatString` and not `{{count}}`.
 */
const COUNT_KEYS = {
  one: 'groups.d-student-has-been-released',
  other: 'groups.d-students-have-been-released',
} as const;

/**
 * `forbidden` is the reachable refusal: the `/admin/*` layout guard admits the
 * group's own study helper while the endpoint admits platform admins only.
 * Everything else, including 400 `study_group_has_no_study_helper`, falls to
 * the generic message.
 */
const FAILURE_COPY = {
  forbidden: {
    title: 'groups.tag-permission-denied-title',
    body: 'wbs.you-dont-have-permission',
  },
} as const;

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/admin/release',
)({
  component: ReleaseStudentsScreenGate,
});

/**
 * The Settings layout admits the group's own study helper, so the screen
 * refuses everyone but platform admins before its queries and form exist.
 */
function ReleaseStudentsScreenGate(): React.ReactNode {
  const { t } = useTranslation();
  const { isAdmin, isPending } = useCurrentUser();
  if (isPending) return null;
  if (!isAdmin) {
    return (
      <PageContent>
        <AlertBox variant="error">{t('wbs.you-dont-have-permission')}</AlertBox>
      </PageContent>
    );
  }
  return <ReleaseStudentsForm />;
}

function ReleaseStudentsForm(): React.ReactNode {
  const { t } = useTranslation();
  const { groupName } = Route.useParams();
  const navigate = useNavigate();
  const adminTabs = useAdminTabs();

  const { data: groupData } = api.getGroupBySlug.useQuery({
    path: { group_slug: groupName },
  });
  const groupId = groupData?.id ?? 0;

  const studentPicker = useGroupStudentPicker(groupId);

  // Counts the same population the write iterates, under the screen's own
  // gate. Release ignores `graduated_students`.
  const { data: counts } = api.getGroupStudentSelectionCounts.useQuery(
    { path: { group_id: groupId } },
    { enabled: groupId > 0 },
  );

  const [allStudents, setAllStudents] = useState(false);
  const [confirming, setConfirming] = useState<{
    body: GroupReleaseRequest;
    count: number;
  } | null>(null);
  const [result, setResult] = useState<GroupReleaseResult | null>(null);
  const [selectionInvalid, setSelectionInvalid] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  // Set when the last run was the 'all' selection, which the server caps per
  // request; Run again reposts it until `remaining` reaches 0.
  const [lastWasAll, setLastWasAll] = useState(false);

  const { mutateAsync: releaseStudents, isPending: isReleasing } =
    api.releaseGroupStudents.useMutation();

  const handleSave = (): void => {
    setFailure(null);
    setResult(null);
    const studentIds = studentPicker.value.map(Number);
    // The all-students arm is refused on an empty population too. `counts` is
    // only consulted once resolved; until then the server answers and the
    // count-0 banner below is the backstop.
    const selectedCount = allStudents
      ? counts?.all_students
      : studentIds.length;
    if (selectedCount === 0) {
      setSelectionInvalid(true);
      return;
    }
    setSelectionInvalid(false);
    setConfirming({
      body: allStudents
        ? { selection: 'all', student_ids: [] }
        : { selection: 'selected', student_ids: studentIds },
      count: selectedCount ?? 0,
    });
  };

  const handleConfirm = async (
    request: GroupReleaseRequest | undefined = confirming?.body,
  ): Promise<void> => {
    if (request === undefined) return;
    setFailure(null);
    try {
      const written = await releaseStudents({
        path: { group_id: groupId },
        body: request,
      });
      setConfirming(null);
      setResult(written);
      setLastWasAll(request.selection === 'all');
      studentPicker.onChange([]);
      setAllStudents(false);
      await Promise.all([
        api.getGroupStudents.invalidateQueries(),
        api.getGroupStudentSelectionCounts.invalidateQueries(),
        // `students_adopted` reads the column this write clears.
        api.getGroupStudyHelpers.invalidateQueries(),
      ]);
    } catch (error) {
      setConfirming(null);
      setFailure(error);
    }
  };

  const failureCode = failure !== null ? errorCode(failure) : undefined;
  const failureCopy =
    failureCode !== undefined && failureCode in FAILURE_COPY
      ? FAILURE_COPY[failureCode as keyof typeof FAILURE_COPY]
      : undefined;

  // A 200 is not a success: a resolved-empty selection comes back with
  // `released: 0` and means "nothing selected".
  const released = result?.released ?? 0;
  const skipped = result?.skipped ?? [];
  const emptySelection =
    selectionInvalid ||
    (result !== null && released === 0 && skipped.length === 0);

  const skippedList = skipped.length > 0 && (
    <SkippedList>
      {skipped.map((student) => (
        <li key={student.student_id}>
          {student.display_name || t('actions.deleted-user')} —{' '}
          {t(SKIP_REASON_KEYS[student.reason])}
        </li>
      ))}
    </SkippedList>
  );

  return (
    <PageContent>
      <TabsBar tabs={adminTabs} base="/groups/$groupName/admin" />
      <GroupHeaderWrapper>
        <TitleWithQuestion title={t('groups.release')} />
      </GroupHeaderWrapper>
      {/* Legacy redirects silently on an empty selection; the sibling
          screen's sentence is shown instead. */}
      {emptySelection && (
        <AlertBox variant="error">
          {t('groups.please-select-at-least-one-student')}
        </AlertBox>
      )}
      {failure !== null && (
        <AlertBox variant="error">
          <div>
            <strong>
              {failureCopy
                ? t(failureCopy.title)
                : t('wbs.something-went-wrong-please')}
            </strong>
            {failureCopy && <div>{t(failureCopy.body)}</div>}
          </div>
        </AlertBox>
      )}
      {released > 0 && (
        <AlertBox variant="success">
          <div>
            <strong>
              {formatString(
                t(released === 1 ? COUNT_KEYS.one : COUNT_KEYS.other),
                String(released),
              )}
            </strong>
            {skippedList}
          </div>
        </AlertBox>
      )}
      {lastWasAll && (result?.remaining ?? 0) > 0 && (
        <AlertBox variant="info">
          <RemainingRow>
            <span data-testid="remaining-banner">
              {formatString(
                t('groups.d-students-remain'),
                String(result?.remaining ?? 0),
              )}
            </span>
            <WBSButton
              type="button"
              alternate
              isLoading={isReleasing}
              onClick={() => {
                void handleConfirm({ selection: 'all', student_ids: [] });
              }}
            >
              {t('groups.run-again')}
            </WBSButton>
          </RemainingRow>
        </AlertBox>
      )}
      {/* Nothing applied, but something was named: the skip list is the whole
          explanation, so it renders without a success banner over it. */}
      {released === 0 && skipped.length > 0 && (
        <AlertBox variant="warning">
          <div>{skippedList}</div>
        </AlertBox>
      )}
      <InfoBox>
        <LargeArrowBackButton
          onClick={() =>
            navigate({
              to: '/groups/$groupName/admin/group-avatar/',
              params: { groupName },
            })
          }
        />
        <CheckRow>
          <CheckboxLabel>
            <WBSCheckbox
              checked={allStudents}
              onChange={(e) => setAllStudents(e.target.checked)}
            />
            {t('lessons.all-students')}
          </CheckboxLabel>
          <Counter data-testid="all-students-count">
            {counts?.all_students ?? 0}
          </Counter>
        </CheckRow>
        <Field>
          <FormFieldLabel>{t('lessons.students')}</FormFieldLabel>
          {/* Ticking All Students locks the picker. `onChange` is neutralised
              too, because `SearchSelect` keeps each chip's remove button live
              while disabled. */}
          <SearchSelect
            multi
            disabled={allStudents}
            loading={studentPicker.loading}
            minQueryLength={PEOPLE_PICKER_MIN_QUERY}
            options={studentPicker.options}
            value={studentPicker.value}
            onChange={allStudents ? noop : studentPicker.onChange}
            onSearch={studentPicker.onSearch}
            placeholder={t('lessons.select-students')}
          />
        </Field>
        <Footer>
          <WBSButton type="button" wide onClick={handleSave}>
            {t('groups.confirm-release-to-student-board')}
          </WBSButton>
        </Footer>
      </InfoBox>
      {confirming !== null && (
        <BulkStudentConfirmModal
          title={t('groups.release')}
          count={confirming.count}
          isConfirming={isReleasing}
          onConfirm={() => {
            void handleConfirm();
          }}
          onCancel={() => setConfirming(null)}
        />
      )}
    </PageContent>
  );
}

const InfoBox = styled(InfoSectionContainer)`
  position: relative;
`;

const CheckRow = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
`;

const Counter = styled.span`
  color: var(--grey-600);
  font-size: 13px;
`;

const Field = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
`;

const RemainingRow = styled.div`
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: space-between;
`;

const SkippedList = styled.ul`
  margin: 8px 0 0 0;
  padding-left: 20px;
`;
